import type {
  Action,
  ValidationError,
  ValidationResult,
} from "../../types/actions";
import type { MediaItem, Project } from "../../types/project";
import { createInverseAction, registerActionHandler } from "../registry";
import type { ActionHandler, ActionHandlerContext } from "../registry";

/**
 * The media library sits outside the timeline, so there is no Mutable* helper
 * to go through: these handlers assign project.mediaLibrary.items the way the
 * executor's switch did. The array is replaced rather than edited in place
 * because LibraryMediaPreview selects it straight out of the store and
 * AspectRatioMenu and SaveTemplateDialog carry it as a memo dependency, so a
 * splice would change the library without repainting it.
 */

function mediaItems(project: Project): readonly MediaItem[] {
  return project.mediaLibrary.items;
}

function setMediaItems(project: Project, next: MediaItem[]): void {
  (project.mediaLibrary as { items: MediaItem[] }).items = next;
}

function findMedia(project: Project, mediaId: string): MediaItem | undefined {
  return project.mediaLibrary.items.find((item) => item.id === mediaId);
}

const ok = (): ValidationResult => ({ valid: true, errors: [] });

const invalid = (
  code: string,
  message: string,
  path: string,
): ValidationResult => ({
  valid: false,
  errors: [{ code, message, path }],
});

/**
 * Not the inferMediaType exported from media/, which takes a mime string,
 * matches it against a whitelist of container types and returns null for
 * anything else. This is the executor's: a prefix match that falls back to
 * "video" for a container it does not recognise, so an odd file still imports.
 * Swapping in the stricter one would change which files can be added at all.
 */
function inferMediaType(file: File): "video" | "audio" | "image" {
  if (file.type.startsWith("video/")) return "video";
  if (file.type.startsWith("audio/")) return "audio";
  if (file.type.startsWith("image/")) return "image";
  return "video";
}

/**
 * Field by field, as the inverse generator wrote it, which means an undone
 * delete loses every field not listed here: filmstripThumbnails and the KieAI
 * in-flight flags, which is harmless, but also isPlaceholder, originalUrl and
 * sourceFile, which are persisted and are how a project relinks an item to a
 * file on another machine. cloneClip in the inverse generator spreads first
 * for exactly the opposite reason. This is a move, not a fix, so the list
 * stands.
 * The nested metadata and waveform are copied so the restored item shares
 * nothing mutable with the snapshot the inverse was generated from; the blob
 * is shared, because copying it would duplicate the bytes the library exists
 * to hold once.
 */
function cloneMediaItem(item: MediaItem): Record<string, unknown> {
  return {
    id: item.id,
    name: item.name,
    type: item.type,
    fileHandle: item.fileHandle,
    blob: item.blob,
    metadata: { ...item.metadata },
    thumbnailUrl: item.thumbnailUrl,
    waveformData: item.waveformData
      ? new Float32Array(item.waveformData)
      : null,
  };
}

function validateMediaId(action: Action, project: Project): ValidationError[] {
  const mediaId = (action.params as { mediaId?: unknown }).mediaId;

  if (!mediaId || typeof mediaId !== "string") {
    return [
      {
        code: "INVALID_PARAMS",
        message: "Media ID is required and must be a string",
        path: "params.mediaId",
      },
    ];
  }

  if (!findMedia(project, mediaId)) {
    return [
      {
        code: "MEDIA_NOT_FOUND",
        message: `Media with ID ${mediaId} not found`,
        path: "params.mediaId",
      },
    ];
  }

  return [];
}

const mediaImport: ActionHandler = {
  type: "media/import",

  // Truthiness, not an instanceof File check: the legacy validator asked for
  // no more than that, and the callers that hand it a file-shaped object would
  // start failing on a stricter one.
  validate(action: Action): ValidationResult {
    const file = (action.params as { file?: unknown }).file;
    return file
      ? ok()
      : invalid(
          "INVALID_PARAMS",
          "File is required for media import",
          "params.file",
        );
  },

  apply(action: Action, project: Project, ctx: ActionHandlerContext): void {
    const params = action.params as { file: File; mediaId?: string };
    const newMediaItem: MediaItem = {
      id: params.mediaId ?? `media-${crypto.randomUUID()}`,
      name: params.file.name,
      type: inferMediaType(params.file),
      fileHandle: null,
      blob: params.file,
      metadata: {
        duration: 0,
        width: 0,
        height: 0,
        frameRate: 0,
        codec: "",
        sampleRate: 0,
        channels: 0,
        fileSize: params.file.size,
      },
      thumbnailUrl: null,
      waveformData: null,
    };

    // Redo replays this same action object; without this it would mint a fresh
    // id and the stored inverse would target the one from the first run.
    (action.params as Record<string, unknown>).mediaId = newMediaItem.id;

    setMediaItems(project, [...mediaItems(project), newMediaItem]);
    ctx.lastAddedIds.set("media", newMediaItem.id);
  },

  invert(action: Action): Action | null {
    // The executor resolves "__LAST_ADDED__" against the id recorded under the
    // "media" category, while this import is still the latest one.
    return createInverseAction(action, "media/delete", {
      mediaId: "__LAST_ADDED__",
    });
  },
};

const mediaDelete: ActionHandler = {
  type: "media/delete",

  validate(action: Action, project: Project): ValidationResult {
    const errors = validateMediaId(action, project);
    return { valid: errors.length === 0, errors };
  },

  apply(action: Action, project: Project): void {
    const params = action.params as { mediaId: string };
    setMediaItems(
      project,
      mediaItems(project).filter((item) => item.id !== params.mediaId),
    );
  },

  invert(action: Action, projectBefore: Project): Action | null {
    const params = action.params as { mediaId: string };
    const deletedMedia = findMedia(projectBefore, params.mediaId);
    if (!deletedMedia) return null;
    return createInverseAction(action, "media/restore", {
      mediaItem: cloneMediaItem(deletedMedia),
    });
  },
};

const mediaRename: ActionHandler = {
  type: "media/rename",

  // Both errors are collected rather than returning on the first: a rename of
  // a missing item with a missing name reported both, and the store joins the
  // messages into the one string the user sees.
  validate(action: Action, project: Project): ValidationResult {
    const errors = validateMediaId(action, project);
    const name = (action.params as { name?: unknown }).name;

    if (!name || typeof name !== "string") {
      errors.push({
        code: "INVALID_PARAMS",
        message: "Media name is required and must be a string",
        path: "params.name",
      });
    }

    return { valid: errors.length === 0, errors };
  },

  apply(action: Action, project: Project): void {
    const params = action.params as { mediaId: string; name: string };
    setMediaItems(
      project,
      mediaItems(project).map((item) =>
        item.id === params.mediaId ? { ...item, name: params.name } : item,
      ),
    );
  },

  invert(action: Action, projectBefore: Project): Action | null {
    const params = action.params as { mediaId: string };
    const media = findMedia(projectBefore, params.mediaId);
    if (!media) return null;
    return createInverseAction(action, "media/rename", {
      mediaId: params.mediaId,
      name: media.name,
    });
  },
};

/**
 * The inverse of a delete, minted only by mediaDelete.invert and never by a
 * caller, which is why neither legacy switch had a case for it: undo applies an
 * inverse straight through applyAction, skipping both the validator and the
 * inverse generator. The validate and invert below are new, and they are worth
 * writing even though nothing executes a restore today, because the day
 * something does, the entry it pushes carries a null inverse and undo refuses
 * to pop it: the stack dead-ends there. The apply is the executor's, appending
 * rather than putting the item back at the index it was removed from, which is
 * a wart the marker domain fixed for markers and this deliberately leaves.
 */
const mediaRestore: ActionHandler = {
  type: "media/restore",

  validate(action: Action): ValidationResult {
    const mediaItem = (action.params as { mediaItem?: MediaItem }).mediaItem;
    return mediaItem && typeof mediaItem.id === "string"
      ? ok()
      : invalid(
          "INVALID_PARAMS",
          "media/restore requires a mediaItem with an id",
          "params.mediaItem",
        );
  },

  apply(action: Action, project: Project): void {
    const params = action.params as { mediaItem: MediaItem };
    setMediaItems(project, [...mediaItems(project), params.mediaItem]);
  },

  invert(action: Action): Action | null {
    const mediaItem = (action.params as { mediaItem?: MediaItem }).mediaItem;
    if (!mediaItem || typeof mediaItem.id !== "string") return null;
    return createInverseAction(action, "media/delete", {
      mediaId: mediaItem.id,
    });
  },
};

for (const handler of [mediaImport, mediaDelete, mediaRename, mediaRestore]) {
  registerActionHandler(handler);
}
